import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/base/buttons/button';
import { PinInput } from '@/components/base/pin-input/pin-input';
import {
  WorkEmailCodeThrottledError,
  useSendWorkEmailVerification,
} from '@/hooks/api/use-send-work-email-verification';
import { useVerifyWorkEmailCode } from '@/hooks/api/use-verify-work-email-code';

/**
 * Matches the server's send cooldown
 * (`WORK_EMAIL_CODE_SEND_COOLDOWN_SECONDS`). Kept in step so the button
 * re-enables at roughly the moment another send would be accepted.
 */
const RESEND_COOLDOWN_SECONDS = 60;

const CODE_LENGTH = 6;

const codeSchema = z
  .string()
  .regex(
    new RegExp(`^\\d{${CODE_LENGTH}}$`),
    t`Verification code must be ${CODE_LENGTH} digits`,
  );

interface WorkEmailCodeFormProps {
  profileId: string;
  workEmail: string;
  /**
   * A code was sent moments ago, so start the resend countdown on mount and
   * spare the participant a pointless 429. Pass false when the outstanding code
   * is merely known to exist — it may be old, and they should be able to ask for
   * a fresh one straight away.
   */
  codeJustSent?: boolean;
  onVerified: () => void;
}

export function WorkEmailCodeForm({
  profileId,
  workEmail,
  codeJustSent = false,
  onVerified,
}: WorkEmailCodeFormProps) {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(
    codeJustSent ? RESEND_COOLDOWN_SECONDS : 0,
  );

  const sendCode = useSendWorkEmailVerification();
  const verifyCode = useVerifyWorkEmailCode();

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    const parsed = codeSchema.safeParse(code);
    if (!parsed.success) {
      setErrorMessage(
        parsed.error.issues[0]?.message ?? t`Invalid verification code`,
      );
      return;
    }

    setErrorMessage(null);
    try {
      await verifyCode.mutateAsync({ profileId, code });
      onVerified();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : t`An unknown error occurred`,
      );
    }
  };

  const handleResend = async () => {
    setErrorMessage(null);
    setCode('');
    try {
      await sendCode.mutateAsync({ profileId });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      // Throttled means a code went out moments ago — that one is still
      // valid, so start the countdown instead of surfacing an error.
      if (error instanceof WorkEmailCodeThrottledError) {
        setCooldown(RESEND_COOLDOWN_SECONDS);
        return;
      }

      setErrorMessage(
        error instanceof Error ? error.message : t`An unknown error occurred`,
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-secondary">
        <Trans>
          We sent a {CODE_LENGTH}-digit code to <strong>{workEmail}</strong>.
          Enter it below to verify your work email.
        </Trans>
      </p>

      <div className="mt-4 flex flex-col items-center gap-1.5">
        {/*
          This form lives in a modal, unlike PhoneVerification which sits in a
          wide page column. PinInput's `sm` slots jump to `size-16` at the `sm`
          breakpoint, which at six digits is wider than the dialog — so cap the
          slots and drop the separator to keep the row inside it.
        */}
        <PinInput size="sm" data-testid="participant-work-email-pin">
          <PinInput.Group
            maxLength={CODE_LENGTH}
            value={code}
            onChange={setCode}
            containerClassName="h-10 gap-1.5 sm:h-12 sm:gap-2"
          >
            {Array.from({ length: CODE_LENGTH }, (_, index) => (
              <PinInput.Slot
                key={index}
                index={index}
                className="size-10 text-display-sm sm:size-12"
              />
            ))}
          </PinInput.Group>
        </PinInput>
        {errorMessage && (
          <p className="text-center text-sm text-error-primary">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-3">
        <Button
          color="primary"
          size="lg"
          className="w-full"
          isLoading={verifyCode.isPending}
          isDisabled={verifyCode.isPending || code.length < CODE_LENGTH}
          onClick={() => void handleVerify()}
          data-testid="participant-work-email-confirm-button"
        >
          <Trans>Verify work email</Trans>
        </Button>

        <Button
          color="tertiary"
          size="md"
          className="w-full"
          isLoading={sendCode.isPending}
          isDisabled={sendCode.isPending || cooldown > 0}
          onClick={() => void handleResend()}
          data-testid="participant-work-email-resend-button"
        >
          {cooldown > 0 ? (
            <Trans>Resend code in {cooldown}s</Trans>
          ) : (
            <Trans>Send a new code</Trans>
          )}
        </Button>
      </div>
    </div>
  );
}
